/**
 * AgentChatPanel Component
 * 
 * Displays agent conversation history in a Jules/Copilot Workspace style.
 * Uses json-render components for rich UI elements.
 */

import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Textarea } from '@pagespeedify/ui';
import {
    Bot,
    User,
    Check,
    Loader2,
    XCircle,
    AlertCircle,
    AlertTriangle,
    Send,
    Plus,
    Paperclip,
    FileText,
    Image as ImageIcon,
    X,
    Maximize2,
    Minimize2,
} from 'lucide-react';
import { cn } from '@pagespeedify/ui';
import { JsonRenderMessage, MarkdownRenderer, PlanAccordion, PRPreviewCard } from '../json-render';
import ChatLimitsWidget from './ChatLimitsWidget';
import type { ClarificationSelectionDraft, JsonRenderComponent, VerificationUpdateAction, VerificationUpdateCheck } from '../../types/json-render';
import { toast } from 'sonner';

// Types matching backend agent_messages schema
export interface AgentMessage {
    id: string;
    task_id: string;
    user_id?: string;
    session_id?: string;
    role: 'user' | 'assistant' | 'system';
    type: 'clarification' | 'status' | 'progress' | 'error' | 'plan' | null;
    content: string;
    metadata: {
        planId?: string;
        summary?: string;
        stepCount?: number;
        steps?: Array<{
            id: string;
            title: string;
            description?: string;
            action: string;
            status?: 'pending' | 'in_progress' | 'running' | 'completed' | 'failed' | 'skipped' | 'blocked';
            statusReason?: string;
            executionLane?: 'local' | 'remote' | 'blocked';
        }>;
        components?: JsonRenderComponent[];
        fileChanges?: Array<{
            name: string;
            path: string;
            status: 'added' | 'modified' | 'deleted';
            diff?: string;
        }>;
        stepId?: string;
        stepIndex?: number;
        status?: string;
        error?: string;
        prUrl?: string;
        branch?: string;
        prTitle?: string;
        [key: string]: any;
    };
    created_at: string;
}

import type {
    AgentBatchPlanTreeStep,
    AgentClarificationContext,
    AgentClarificationTarget,
    AgentPRDraft,
    AgentPlan,
    AgentStatusResponse,
    AttachmentImageFile,
    AttachmentTextFile,
    PromptAttachments
} from '../../types/agent';

type AgentTask = AgentStatusResponse;

interface AgentChatPanelProps {
    messages: AgentMessage[];
    isLoading?: boolean;
    plan?: AgentPlan;
    onApprove?: () => void;
    onCancel?: () => void;
    isApproving?: boolean;
    isRejecting?: boolean;
    onSendMessage?: (message: string, options?: {
        promptAttachments?: PromptAttachments;
        clarificationTarget?: AgentClarificationTarget | null;
    }) => Promise<void> | void;
    composerIntent?: 'discuss' | 'execute';
    onComposerIntentChange?: (intent: 'discuss' | 'execute') => void;
    showComposerIntentToggle?: boolean;
    inputPlaceholder?: string;
    isSending?: boolean;
    canSendMessage?: boolean;
    onCancelTask?: () => void;
    /** Submit PR button handler — shown when execution is complete and PR not yet created */
    onSubmitPR?: () => void;
    /** Whether the task is ready for PR submission */
    isSubmittingPR?: boolean;
    /** URL of the created PR — shows 'View on GitHub' button */
    prUrl?: string;
    /** Draft PR metadata shown before PR is published */
    prDraft?: AgentPRDraft | null;
    /** When true, input is unlocked for discussion even if a plan message exists */
    isInDiscussionMode?: boolean;
    /** Real-time streaming message content from AI */
    streamingMessage?: string;
    /** Whether a streaming response is currently in progress */
    isStreaming?: boolean;
    /** User's pending message being processed (shows while streaming) */
    pendingUserMessage?: string;
    /** Current plan version number */
    planVersion?: number;
    /** Active plan version from backend status */
    activePlanVersion?: number | null;
    /** Active batch id from backend status */
    activeBatchId?: string | null;
    /** Total number of plan versions */
    totalPlanVersions?: number;
    /** Active batch plan index from worker snapshot */
    activePackageIndex?: number | null;
    /** Total concrete plans in the active batch */
    activePackageTotal?: number | null;
    /** JSON-render components from streaming response */
    streamingComponents?: Array<{ type: string; id: string; props: Record<string, any> }>;
    /** Callback when user clicks a clarification option */
    onClarificationSubmit?: (option: {
        value: string;
        label?: string;
        actionId?: string;
        expiresAt?: string;
        variant?: string;
        selectedIssueIds?: string[];
        selectedScopePaths?: string[];
        selectedBatchId?: string;
        selectedGoal?: 'performance' | 'security' | 'debugging' | 'stability';
        monitoringId?: string;
        monitoringUrl?: string;
    }) => void;
    /** Active clarification action IDs from latest status poll */
    activeClarificationActionIds?: string[];
    /** Active clarification action values from latest status poll */
    activeClarificationActionValues?: string[];
    /** Clarification action request in-flight */
    isClarificationLoading?: boolean;
    /** Clarification actionId currently being processed */
    loadingClarificationActionId?: string | null;
    /** User-facing loading message for the current clarification action */
    clarificationLoadingStatusText?: string | null;
    /** Force disable clarification actions for stale banners */
    clarificationForceDisabled?: boolean;
    /** Optional tooltip reason for disabled clarification buttons */
    clarificationDisabledReason?: string;
    /** Current backend status for lifecycle-aware rendering */
    currentStatus?: string;
    /** Structured verification display state from backend snapshot */
    verificationDisplay?: AgentTask['verificationDisplay'];
    /** Whether verification still requires a user decision before PR readiness */
    verificationDecisionRequired?: boolean;
    /** True while approval transition is in-flight (persists across refresh via backend state) */
    approvalTransitionInFlight?: boolean;
    /** PR finalization state from backend status */
    finalizationStatus?: string;
    /** PR finalization error message, if any */
    finalizationError?: string;
    /** True when queued finalization is stale and being requeued */
    finalizationStale?: boolean;
    /** Active clarification overlay context */
    clarificationContext?: AgentClarificationContext | null;
    /** Clarification options for active overlay */
    clarificationOptions?: Array<{
        label: string;
        value: string;
        variant?: string;
        actionId?: string;
        expiresAt?: string;
        selectedBatchId?: string;
        selectedGoal?: "performance" | "security" | "debugging" | "stability";
        disabled?: boolean;
        disabledReason?: string;
    }>;
    /** Discussion-only actions while clarification is unresolved */
    discussionActions?: Array<{
        label: string;
        value: string;
        actionId?: string;
        expiresAt?: string;
    }>;
    /** Discussion action used to resume a dismissed clarification overlay */
    resumeDiscussionAction?: {
        label: string;
        value: string;
        actionId?: string;
        expiresAt?: string;
    } | null;
    /** Render clarification overlay over composer when true */
    showClarificationOverlay?: boolean;
    /** True while approval decision tokens are being hydrated from worker snapshot */
    approvalDecisionRefreshPending?: boolean;
    /** True when clarification overlay is dismissed into chat mode */
    isClarificationOverlayDismissed?: boolean;
    /** Show the resume/return affordance for a decision dismissed into discussion mode */
    showClarificationResumePill?: boolean;
    /** Dismiss active clarification overlay into discussion mode */
    onDismissClarificationOverlay?: () => void;
    /** Restore the clarification overlay from dismissed state */
    onResumeClarificationOverlay?: () => void;
    /** Submit clarification discussion input (non-mutating) */
    onClarificationDiscussSubmit?: (input: string) => void;
    /** Notify parent to show resume cue after clarification chat */
    onClarificationResumeCue?: (cue: { contextId: string; submittedAt: string } | null) => void;
    /** Submit tokenized discussion action (for example Create plan) */
    onDiscussionActionSubmit?: (action: {
        value: string;
        label?: string;
        actionId?: string;
        expiresAt?: string;
        variant?: string;
    }) => void;
    /** Discussion action request in-flight */
    isDiscussionActionLoading?: boolean;
    /** Current selection draft captured from inline clarification controls */
    clarificationSelectionDraft?: ClarificationSelectionDraft | null;
    /** Current active goal for same-task continuation */
    activeGoal?: 'performance' | 'security' | 'debugging' | 'stability' | null;
    /** Goal usage summary for same-task continuation */
    goalSummaries?: Array<{
        goal: 'performance' | 'security' | 'debugging' | 'stability';
        totalIssues: number;
        completedIssues: number;
        remainingIssues: number;
        inProgressIssues: number;
        lastFailureReason: string | null;
        discoveredAt: string | null;
        used?: boolean;
        exhausted?: boolean;
        active?: boolean;
    }>;
    /** Existing batches available for same-task goal changes */
    clarificationBatchOptions?: Array<{
        batchId: string;
        label: string;
        latestGoal?: string | null;
        latestGoalLabel?: string | null;
        currentGoal?: string | null;
        currentGoalLabel?: string | null;
        exhaustedGoals?: string[];
        goalOptions?: Array<{
            value: string;
            label: string;
            disabled?: boolean;
            disabledReason?: string;
        }>;
    }>;
    /** Updates selection draft from inline clarification controls */
    onClarificationSelectionChange?: (draft: ClarificationSelectionDraft) => void;
    /** Updates parent with the currently focused clarification step target */
    onClarificationTargetChange?: (target: AgentClarificationTarget | null) => void;
}

interface ClarificationOverlayChoice {
    key: string;
    label: string;
    kind: 'action' | 'other';
    option?: {
        label: string;
        value: string;
        variant?: string;
        actionId?: string;
        expiresAt?: string;
        disabled?: boolean;
        disabledReason?: string;
        selectedBatchId?: string;
        selectedGoal?: 'performance' | 'security' | 'debugging' | 'stability';
    };
}

function simplifyVerificationCopy(value?: string | null, fallback = 'checks need setup'): string {
    const raw = String(value || '').trim();
    const haystack = raw.toLowerCase();
    if (!raw) return fallback;
    if (
        haystack.includes('preparing local')
        || haystack.includes('local tests verifier')
        || haystack.includes('bootstrap')
        || haystack.includes('pytest')
        || haystack.includes('test setup')
        || haystack.includes('tests need setup')
    ) {
        return 'tests need setup';
    }
    if (
        haystack.includes('python is unavailable')
        || haystack.includes('python runtime')
        || haystack.includes('runtime_unavailable')
        || haystack.includes('runtime unavailable')
    ) {
        return 'Python is unavailable';
    }
    if (haystack.includes('syntax')) return 'syntax failed';
    if (haystack.includes('build')) return 'build failed';
    if (haystack.includes('lint')) return 'lint needs setup';
    if (haystack.includes('type')) return 'type checks need setup';
    return raw.length <= 80 ? raw : fallback;
}

const MAX_TEXT_ATTACHMENTS = 2;
const MAX_IMAGE_ATTACHMENTS = 2;
const MAX_TEXT_FILE_BYTES = 100 * 1024;
const MAX_IMAGE_FILE_BYTES = 2 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 6 * 1024 * 1024;

const ALLOWED_TEXT_EXTENSIONS = new Set(['.txt', '.md', '.log']);
const ALLOWED_TEXT_MIME_TYPES = new Set([
    '',
    'application/octet-stream',
    'application/log',
    'text/plain',
    'text/markdown',
    'text/x-markdown',
    'text/x-log',
]);
const ALLOWED_IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.svg']);
const ALLOWED_IMAGE_MIME_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/svg+xml',
]);

type ClarificationActionOptionValue = {
    label: string;
    value: string;
    variant?: string;
    actionId?: string;
    expiresAt?: string;
    disabled?: boolean;
    disabledReason?: string;
    selectedBatchId?: string;
    selectedPlanVersion?: number;
    planVersion?: number;
    selectedGoal?: 'performance' | 'security' | 'debugging' | 'stability';
};

const ACTION_INTENT_PATTERNS: Record<string, RegExp[]> = {
    done_for_now: [/\b(done for now|done|finish for now|stop for now)\b/]
};
const ACTION_INTENT_PRIORITY: Record<string, number> = {
    done_for_now: 200
};

function formatGoalLabel(goal: string | null | undefined): string {
    if (typeof goal !== 'string' || goal.trim() === '') {
        return 'Not set';
    }
    return goal.trim().replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeIntentText(raw: string): string {
    return raw
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function mapClarificationInputToActionOption(
    inputText: string,
    options: ClarificationActionOptionValue[]
): ClarificationActionOptionValue | null {
    const normalizedInput = normalizeIntentText(inputText);
    if (!normalizedInput) return null;
    const liveOptions = options.filter((option) =>
        typeof option?.value === 'string'
        && option.value.trim() !== ''
        && typeof option?.actionId === 'string'
        && option.actionId.trim() !== ''
    );
    if (liveOptions.length === 0) return null;

    const TEXT_MUTATION_ALLOWLIST = new Set<string>([
        'done_for_now'
    ]);
    const matchedByValue = liveOptions.filter((option) => {
        const key = option.value.trim();
        if (!TEXT_MUTATION_ALLOWLIST.has(key)) return false;
        const patterns = ACTION_INTENT_PATTERNS[key] || [];
        return patterns.some((pattern) => pattern.test(normalizedInput));
    });
    if (matchedByValue.length === 1) {
        return matchedByValue[0];
    }
    if (matchedByValue.length > 1) {
        const ranked = [...matchedByValue].sort((a, b) => {
            const aPriority = ACTION_INTENT_PRIORITY[a.value] || 0;
            const bPriority = ACTION_INTENT_PRIORITY[b.value] || 0;
            return bPriority - aPriority;
        });
        const top = ranked[0];
        if (!top) return null;
        const topPriority = ACTION_INTENT_PRIORITY[top.value] || 0;
        const sameTopCount = ranked.filter(
            (option) => (ACTION_INTENT_PRIORITY[option.value] || 0) === topPriority
        ).length;
        return sameTopCount === 1 ? top : null;
    }

    const matchedByLabel = liveOptions.filter((option) => {
        const normalizedLabel = normalizeIntentText(option.label || '');
        if (!normalizedLabel) return false;
        if (normalizedInput === normalizedLabel || normalizedInput.includes(normalizedLabel)) {
            return true;
        }
        const labelTokens = normalizedLabel.split(' ').filter((token) => token.length >= 4);
        if (labelTokens.length === 0) return false;
        return labelTokens.every((token) => normalizedInput.includes(token));
    });
    if (matchedByLabel.length === 1) {
        return matchedByLabel[0];
    }
    return null;
}
const EXPECTED_IMAGE_MIME_BY_EXTENSION: Record<string, Set<string>> = {
    '.png': new Set(['image/png']),
    '.jpg': new Set(['image/jpeg', 'image/jpg']),
    '.jpeg': new Set(['image/jpeg', 'image/jpg']),
    '.webp': new Set(['image/webp']),
    '.gif': new Set(['image/gif']),
    '.bmp': new Set(['image/bmp']),
    '.svg': new Set(['image/svg+xml']),
};
const DEFAULT_IMAGE_EXTENSION_BY_MIME: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'image/bmp': '.bmp',
    'image/svg+xml': '.svg',
};
const DEFAULT_TEXT_EXTENSION_BY_MIME: Record<string, string> = {
    'text/plain': '.txt',
    'text/markdown': '.md',
    'text/x-markdown': '.md',
    'application/log': '.log',
    'text/x-log': '.log',
};

const CLARIFICATION_OTHER_CHOICE_KEY = '__clarification_other__';

function getExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : '';
}

function normalizeIncomingFileName(file: File, index: number): string {
    const rawName = String(file.name || '').trim();
    if (rawName) return rawName;
    const mimeType = String(file.type || '').toLowerCase();
    const fallbackTextExtension = DEFAULT_TEXT_EXTENSION_BY_MIME[mimeType];
    if (fallbackTextExtension) {
        return `pasted-context-${Date.now()}-${index + 1}${fallbackTextExtension}`;
    }
    const fallbackImageExtension = DEFAULT_IMAGE_EXTENSION_BY_MIME[mimeType] || '.png';
    return `pasted-image-${Date.now()}-${index + 1}${fallbackImageExtension}`;
}

function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

const CHAT_NATIVE_VERIFICATION_PHASES = new Set([
    'post_pr_decision',
    'next_batch_handoff',
    'strict_verification_recovery'
]);

function isChatNativeVerificationPhase(value: unknown): boolean {
    return CHAT_NATIVE_VERIFICATION_PHASES.has(String(value || '').trim().toLowerCase());
}

function isVerificationOverrideOptionValue(value: unknown): boolean {
    return String(value || '').trim().toLowerCase().startsWith('skip_verification_and_create_pr');
}

function normalizePrOverrideActionLabel(value: unknown, fallbackLabel: string): string {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (normalizedValue === 'skip_verification_and_create_pr') return 'Create PR';
    if (normalizedValue === 'skip_verification_and_create_pr_and_continue_to_next_plan') return 'Create PR & Continue to next plan';
    if (normalizedValue === 'skip_verification_and_create_pr_and_continue_to_next_batch') return 'Create PR & Continue to next batch';
    return fallbackLabel;
}

function buildVerificationChecksFromDisplay(verificationDisplay: AgentTask['verificationDisplay']): VerificationUpdateCheck[] {
    const checks = Array.isArray(verificationDisplay?.checks)
        ? verificationDisplay.checks
        : (Array.isArray((verificationDisplay?.summary as any)?.checks)
            ? (verificationDisplay.summary as any).checks
            : null);
    if (Array.isArray(checks)) {
        return checks as VerificationUpdateCheck[];
    }
    const summary = verificationDisplay?.summary && typeof verificationDisplay.summary === 'object'
        ? verificationDisplay.summary as Record<string, unknown>
        : null;
    if (!summary) return [];
    const normalizeState = (value: unknown): VerificationUpdateCheck['state'] => {
        const normalized = String(value || '').trim().toLowerCase();
        if (normalized === 'passed' || normalized === 'failed' || normalized === 'skipped' || normalized === 'blocked' || normalized === 'deferred' || normalized === 'running') {
            return normalized;
        }
        return 'skipped';
    };
    const lanes = [
        ['build', 'Build', summary.build],
        ['types', 'Types', summary.types],
        ['syntax', 'Syntax', summary.syntax],
        ['test', 'Tests', summary.test],
        ['lint', 'Lint', summary.lint]
    ] as const;
    return lanes
        .filter(([, , state]) => typeof state === 'string' && String(state).trim() !== '')
        .map(([kind, label, state]) => ({
            kind,
            label,
            state: normalizeState(state)
        }));
}

function buildVerificationUpdateComponent(params: {
    verificationDisplay: AgentTask['verificationDisplay'];
    clarificationOptions?: AgentTask['clarificationOptions'];
    allowActions: boolean;
}): JsonRenderComponent | null {
    const verificationDisplay = params.verificationDisplay;
    if (!verificationDisplay) return null;
    const checks = buildVerificationChecksFromDisplay(verificationDisplay);
    const actions = params.allowActions && Array.isArray(params.clarificationOptions)
        ? params.clarificationOptions.map((option) => {
            const value = String(option.value || '').trim();
            const label = normalizePrOverrideActionLabel(value, String(option.label || '').trim());
            const verificationOverride = isVerificationOverrideOptionValue(value);
            return {
                label,
                value,
                variant: option.variant,
                actionId: option.actionId,
                expiresAt: option.expiresAt,
                disabled: option.disabled === true,
                disabledReason: option.disabledReason,
                verificationOverride,
                verificationOverrideReason: verificationOverride
                    ? (option.disabledReason || verificationDisplay.confidenceReason || verificationDisplay.body || 'Verification is incomplete or blocked for this repository.')
                    : null,
                selectedBatchId: option.selectedBatchId,
                selectedGoal: option.selectedGoal
            };
        }).filter((option) => option.label && option.value) as VerificationUpdateAction[]
        : [];
    return {
        type: 'verification_update',
        id: `verification-update-${verificationDisplay.cycleId || verificationDisplay.updatedAt || 'live'}`,
        props: {
            lifecycle: verificationDisplay.lifecycle || verificationDisplay.phase || null,
            gateStatus: verificationDisplay.gateStatus || null,
            confidenceLevel: verificationDisplay.confidenceLevel || null,
            confidenceReason: verificationDisplay.confidenceReason || null,
            severity: verificationDisplay.severity || null,
            headline: verificationDisplay.headline || null,
            body: verificationDisplay.body || null,
            highlightNote: verificationDisplay.highlightNote || null,
            safetyNotes: Array.isArray(verificationDisplay.safetyNotes) ? verificationDisplay.safetyNotes : [],
            checks,
            scope: Array.isArray(verificationDisplay.scope) ? verificationDisplay.scope : [],
            provenance: Array.isArray(verificationDisplay.provenance) ? verificationDisplay.provenance : [],
            environmentNotes: Array.isArray(verificationDisplay.environmentNotes) ? verificationDisplay.environmentNotes : [],
            replayCommands: Array.isArray(verificationDisplay.replayCommands) ? verificationDisplay.replayCommands : [],
            actions,
            detailsDefaultOpen: false,
            allowActionsWithoutActiveTokens: params.allowActions
        }
    };
}

// Individual message renderer
function ChatMessage({
    message,
    onApprove,
    onCancel,
    isApproving,
    currentStatus,
    approvalTransitionInFlight,
    activePlanVersion,
    activeBatchId,
    clarificationSourceMessageId,
    clarificationContext,
    onClarificationSubmit,
    onClarificationSelectionChange,
    activeClarificationActionIds,
    activeClarificationActionValues,
    isClarificationLoading,
    loadingClarificationActionId,
    clarificationForceDisabled,
    clarificationDisabledReason,
    suppressActiveClarificationUi = false,
    verificationDisplay,
    clarificationOptions,
    onStepFocusChange,
}: {
    message: AgentMessage;
    onApprove?: () => void;
    onCancel?: () => void;
    isApproving?: boolean;
    currentStatus?: string;
    approvalTransitionInFlight?: boolean;
    activePlanVersion?: number | null;
    activeBatchId?: string | null;
    clarificationSourceMessageId?: string | null;
    clarificationContext?: AgentClarificationContext | null;
    onClarificationSubmit?: (option: {
        value: string;
        label?: string;
        actionId?: string;
        expiresAt?: string;
        variant?: string;
        selectedIssueIds?: string[];
        selectedScopePaths?: string[];
        selectedBatchId?: string;
        selectedGoal?: 'performance' | 'security' | 'debugging' | 'stability';
        monitoringId?: string;
        monitoringUrl?: string;
    }) => void;
    onClarificationSelectionChange?: (draft: ClarificationSelectionDraft) => void;
    activeClarificationActionIds?: string[];
    activeClarificationActionValues?: string[];
    isClarificationLoading?: boolean;
    loadingClarificationActionId?: string | null;
    clarificationForceDisabled?: boolean;
    clarificationDisabledReason?: string;
    suppressActiveClarificationUi?: boolean;
    verificationDisplay?: AgentTask['verificationDisplay'];
    clarificationOptions?: AgentTask['clarificationOptions'];
    onStepFocusChange?: (step: any | null) => void;
}) {
    const { role, type, content, metadata, created_at } = message;
    const messagePlanVersion = Number(metadata?.planVersion);
    const activePlanVersionNumber = Number(activePlanVersion);
    const messageBatchId = typeof metadata?.batchId === 'string' ? metadata.batchId.trim() : '';
    const activeBatchIdNormalized = typeof activeBatchId === 'string' ? activeBatchId.trim() : '';
    const hasPlanVersionIdentity =
        Number.isFinite(messagePlanVersion) && Number.isFinite(activePlanVersionNumber);
    const hasBatchIdentity = !!messageBatchId && !!activeBatchIdNormalized;
    const isDormantPlan =
        (hasPlanVersionIdentity && messagePlanVersion !== activePlanVersionNumber)
        || (hasBatchIdentity && messageBatchId !== activeBatchIdNormalized);
    const clarificationContextMetadata =
        clarificationContext && typeof clarificationContext.metadata === 'object' && clarificationContext.metadata
            ? clarificationContext.metadata as Record<string, unknown>
            : null;
    const currentClarificationPhase =
        typeof clarificationContext?.phase === 'string' && clarificationContext.phase.trim() !== ''
            ? clarificationContext.phase.trim().toLowerCase()
            : (
                typeof clarificationContextMetadata?.phase === 'string' && clarificationContextMetadata.phase.trim() !== ''
                    ? clarificationContextMetadata.phase.trim().toLowerCase()
                    : ''
            );
    const currentClarificationDecisionKind =
        typeof clarificationContextMetadata?.decisionKind === 'string' && clarificationContextMetadata.decisionKind.trim() !== ''
            ? clarificationContextMetadata.decisionKind.trim().toLowerCase()
            : '';
    const currentClarificationPrPublished = clarificationContextMetadata?.prPublicationVerified === true;
    const messageClarificationPhase =
        typeof metadata?.phase === 'string' && metadata.phase.trim() !== ''
            ? metadata.phase.trim().toLowerCase()
            : '';
    const messageClarificationDecisionKind =
        typeof metadata?.decisionKind === 'string' && metadata.decisionKind.trim() !== ''
            ? metadata.decisionKind.trim().toLowerCase()
            : '';
    const messageClarificationPrPublished = metadata?.prPublicationVerified === true;
    const clarificationIdentityMismatch =
        type === 'clarification'
        && (
            (!!currentClarificationPhase && !!messageClarificationPhase && currentClarificationPhase !== messageClarificationPhase)
            || (
                !!currentClarificationDecisionKind
                && !!messageClarificationDecisionKind
                && currentClarificationDecisionKind !== messageClarificationDecisionKind
            )
            || (messageClarificationPrPublished && !currentClarificationPrPublished)
        );

    const timestamp = new Date(created_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    });

    // User message
    if (role === 'user') {
        return (
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">You</span>
                    <span className="text-xs text-muted-foreground ml-auto">{timestamp}</span>
                    {type === 'clarification' && (
                        <span className="text-xs bg-yellow-500/10 text-yellow-600 px-1.5 py-0.5 rounded">
                            Clarification
                        </span>
                    )}
                </div>
                <div className="pl-0">
                    <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
                </div>
            </div>
        );
    }

    // System error message
    if (role === 'system' && type === 'error') {
        return (
            <div className="flex items-start gap-2 text-destructive py-2 bg-destructive/5 rounded-lg p-3">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <div>
                    <p className="text-sm font-medium">Error</p>
                    <p className="text-sm">{content}</p>
                </div>
            </div>
        );
    }

    // Assistant plan message - use json-render component
    if (role === 'assistant' && type === 'plan') {
        const steps = metadata?.steps || [];
        const phases = steps.map((step, idx) => ({
            id: step.id || `step-${idx}`,
            order: idx + 1,
            title: step.title,
            description: step.description,
            status: step.status || 'pending' as const,
            statusReason: step.statusReason,
            executionLane: step.executionLane,
            expanded: false,
        }));

        return (
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Agent</span>
                    <span className="text-xs text-muted-foreground ml-auto">{timestamp}</span>
                </div>
                <div className="pl-0">
                    <MarkdownRenderer
                        content={content}
                        className="prose-p:text-sm prose-p:mb-2 prose-p:leading-relaxed"
                    />
                    <PlanAccordion
                        planId={metadata?.planId || 'plan-1'}
                        title={Number.isFinite(messagePlanVersion)
                            ? `Execution Plan v${messagePlanVersion}`
                            : 'Execution Plan'}
                        phases={phases}
                        status={(metadata?.status || currentStatus || 'pending') as any}
                        showApprovalButton={false}
                        isDormantPlan={isDormantPlan}
                        approvalTransitionInFlight={approvalTransitionInFlight}
                        onStepFocusChange={onStepFocusChange}
                    />
                </div>
            </div>
        );
    }

    // Assistant message with PR (completion)
    if (role === 'assistant' && metadata?.prUrl) {
        return (
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Agent</span>
                    <span className="text-xs text-muted-foreground ml-auto">{timestamp}</span>
                </div>
                <div className="pl-0">
                    <PRPreviewCard
                        branch={metadata.branch || 'feature-branch'}
                        title={metadata.prTitle || content}
                        description=""
                        stats={{
                            additions: metadata.additions || 0,
                            deletions: metadata.deletions || 0,
                            filesChanged: metadata.filesChanged,
                        }}
                        prUrl={metadata.prUrl}
                        showFeedback={true}
                    />
                </div>
            </div>
        );
    }

    // Assistant general message
    if (role === 'assistant') {
        // Check for json-render components in metadata
        const components = metadata?.components as JsonRenderComponent[] | undefined;
        const normalizedComponents = Array.isArray(components) ? components : [];
        const clarificationSourceId = typeof clarificationSourceMessageId === 'string'
            ? clarificationSourceMessageId.trim()
            : '';
        const isHistoricalClarification = type === 'clarification' && (
            isDormantPlan
            || (!!clarificationSourceId && clarificationSourceId !== message.id)
            || clarificationIdentityMismatch
        );
        const effectiveClarificationForceDisabled = clarificationForceDisabled || isHistoricalClarification;
        const effectiveClarificationDisabledReason = isHistoricalClarification
            ? 'Historical clarification'
            : clarificationDisabledReason;
        const effectiveSuppressActiveClarificationUi =
            suppressActiveClarificationUi || isHistoricalClarification;
        const messagePhase = typeof metadata?.phase === 'string'
            ? metadata.phase.trim().toLowerCase()
            : '';
        const shouldRenderVerificationUpdate =
            type === 'clarification'
            && !!clarificationSourceId
            && clarificationSourceId === message.id
            && !clarificationIdentityMismatch
            && isChatNativeVerificationPhase(messagePhase)
            && !!verificationDisplay;
        const replacementVerificationComponent = shouldRenderVerificationUpdate
            ? buildVerificationUpdateComponent({
                verificationDisplay,
                clarificationOptions,
                allowActions: true
            })
            : null;
        const effectiveComponents = replacementVerificationComponent
            ? [replacementVerificationComponent]
            : normalizedComponents;

        return (
            <div
                className={cn(
                    'mb-6',
                    isHistoricalClarification && 'opacity-70'
                )}
                data-historical-clarification={isHistoricalClarification ? 'true' : undefined}
            >
                <div className="flex items-center gap-3 mb-2">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Agent</span>
                    <span className="text-xs text-muted-foreground ml-auto">{timestamp}</span>
                </div>
                <div className="pl-0">
                    <JsonRenderMessage
                        components={effectiveComponents}
                        fallbackMarkdown={content}
                        onClarificationSubmit={onClarificationSubmit}
                        onClarificationSelectionChange={onClarificationSelectionChange}
                        activeClarificationActionIds={activeClarificationActionIds}
                        activeClarificationActionValues={activeClarificationActionValues}
                        isClarificationLoading={isClarificationLoading}
                        loadingClarificationActionId={loadingClarificationActionId}
                        clarificationForceDisabled={effectiveClarificationForceDisabled}
                        clarificationDisabledReason={effectiveClarificationDisabledReason}
                        suppressActiveClarificationUi={effectiveSuppressActiveClarificationUi}
                    />
                </div>
            </div>
        );
    }

    // Fallback
    return (
        <div className="text-sm text-muted-foreground py-1">
            {content}
        </div>
    );
}

export default function AgentChatPanel({
    messages,
    isLoading = false,
    onApprove,
    onCancel,
    isApproving = false,
    onSendMessage,
    inputPlaceholder = 'Talk to Swayashin Agent...',
    isSending = false,
    canSendMessage = true,
    plan,
    onCancelTask,
    onSubmitPR,
    isSubmittingPR = false,
    prUrl,
    prDraft = null,
    isInDiscussionMode = false,
    streamingMessage,
    isStreaming = false,
    pendingUserMessage,
    planVersion,
    activePlanVersion = null,
    activeBatchId = null,
    totalPlanVersions,
    activePackageIndex = null,
    activePackageTotal = null,
    streamingComponents = [],
    onClarificationSubmit,
    activeClarificationActionIds = [],
    activeClarificationActionValues = [],
    isClarificationLoading = false,
    loadingClarificationActionId = null,
    clarificationLoadingStatusText = null,
    clarificationForceDisabled = false,
    clarificationDisabledReason,
    currentStatus,
    verificationDisplay = null,
    verificationDecisionRequired = false,
    approvalTransitionInFlight = false,
    finalizationStatus,
    finalizationError,
    finalizationStale = false,
    clarificationContext = null,
    clarificationOptions = [],
    discussionActions = [],
    resumeDiscussionAction = null,
    showClarificationOverlay = false,
    approvalDecisionRefreshPending = false,
    isClarificationOverlayDismissed = false,
    showClarificationResumePill = false,
    onDismissClarificationOverlay,
    onResumeClarificationOverlay,
    onClarificationDiscussSubmit,
    onClarificationResumeCue,
    onDiscussionActionSubmit,
    isDiscussionActionLoading = false,
    clarificationSelectionDraft = null,
    activeGoal = null,
    clarificationBatchOptions = [],
    onClarificationSelectionChange,
    onClarificationTargetChange,
    className
}: AgentChatPanelProps & { className?: string }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const attachmentInputRef = useRef<HTMLInputElement>(null);
    const composerMenuRef = useRef<HTMLDivElement>(null);
    const clarificationOverlayRef = useRef<HTMLDivElement>(null);
    const lastClarificationOverlayContextIdRef = useRef<string>('');
    const lastClarificationOverlayFingerprintRef = useRef<string>('');
    const clarificationSubmitGuardRef = useRef<{
        contextId: string;
        actionKey: string;
        submittedAt: number;
    } | null>(null);
    const latestSelectedClarificationChoiceRef = useRef<ClarificationOverlayChoice | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [instructionFiles, setInstructionFiles] = useState<AttachmentTextFile[]>([]);
    const [imageFiles, setImageFiles] = useState<AttachmentImageFile[]>([]);
    const [isComposerMenuOpen, setIsComposerMenuOpen] = useState(false);
    const [clarificationInputValue, setClarificationInputValue] = useState('');
    const [selectedClarificationChoiceKey, setSelectedClarificationChoiceKey] = useState<string>('');
    const [clarificationSelectionResetReason, setClarificationSelectionResetReason] = useState<'options_updated' | null>(null);
    const [clarificationOverlayView, setClarificationOverlayView] = useState<'normal' | 'expanded'>('expanded');

    // Debug: Log streaming state changes
    useEffect(() => {
        console.log('[AgentChatPanel] Streaming state:', { isStreaming, streamingMessageLength: streamingMessage?.length });
    }, [isStreaming, streamingMessage]);

    const phases = useMemo(() => {
        if (Array.isArray(plan?.phases) && plan.phases.length > 0) {
            return plan.phases;
        }
        const planSteps = Array.isArray((plan as any)?.steps)
            ? ((plan as any).steps as AgentBatchPlanTreeStep[])
            : [];
        if (planSteps.length === 0) {
            return [];
        }
        return planSteps.map((step, idx) => ({
            id: step.id || `step-${idx + 1}`,
            order: idx + 1,
            title: step.title || `Step ${idx + 1}`,
            description: step.description,
            status: (step.status === 'failed'
                ? 'failed'
                : step.status === 'completed'
                ? 'completed'
                : step.status === 'skipped'
                ? 'skipped'
                : step.status === 'running' || step.status === 'in_progress'
                ? 'in_progress'
                : 'pending') as 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped',
            statusReason: step.statusReason,
            executionLane: step.executionLane,
            expanded: false,
        }));
    }, [plan]);
    const handlePlanStepFocusChange = useCallback((phase: any | null) => {
        if (!onClarificationTargetChange) return;
        if (!phase || typeof phase !== 'object') {
            onClarificationTargetChange(null);
            return;
        }
        onClarificationTargetChange({
            stepId: typeof phase.id === 'string' && phase.id.trim() !== '' ? phase.id.trim() : null,
            stepOrder: Number.isFinite(Number(phase.order)) ? Number(phase.order) : null,
            stepTitle: typeof phase.title === 'string' && phase.title.trim() !== '' ? phase.title.trim() : null,
        });
    }, [onClarificationTargetChange]);
    useEffect(() => {
        onClarificationTargetChange?.(null);
    }, [onClarificationTargetChange, plan?.id, activePlanVersion, activeBatchId]);
    const activePlanTitle = useMemo(() => {
        const planOrdinal =
            Number.isFinite(Number(activePackageIndex)) && Number(activePackageIndex) >= 0
                ? Number(activePackageIndex) + 1
                : null;
        const planTotal =
            Number.isFinite(Number(activePackageTotal)) && Number(activePackageTotal) > 0
                ? Number(activePackageTotal)
                : null;
        if (planOrdinal && planTotal) {
            return `Execution Plan (${planOrdinal} of ${planTotal})`;
        }
        if (planTotal === 1) {
            return 'Execution Plan (1 of 1)';
        }
        return 'Execution Plan';
    }, [activePackageIndex, activePackageTotal]);

    // Auto-scroll to bottom when new messages arrive or streaming content updates
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [messages, streamingMessage, pendingUserMessage, streamingComponents, isStreaming]);

    useEffect(() => {
        if (!isComposerMenuOpen) return;
        const onPointerDown = (event: MouseEvent) => {
            if (!composerMenuRef.current) return;
            if (!composerMenuRef.current.contains(event.target as Node)) {
                setIsComposerMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [isComposerMenuOpen]);

    // Check if we have a pending plan message that matches the active plan version
    const pendingPlanMessage = (() => {
        const activePlanVersionNumber = Number(activePlanVersion);
        const hasActivePlanVersion = Number.isFinite(activePlanVersionNumber);
        for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
            const message = messages[idx];
            if (message.role !== 'assistant' || message.type !== 'plan') continue;
            if (!hasActivePlanVersion) return message;
            const messagePlanVersionRaw = (message as any)?.metadata?.version ?? (message as any)?.metadata?.planVersion;
            const messagePlanVersion = Number(messagePlanVersionRaw);
            if (Number.isFinite(messagePlanVersion) && messagePlanVersion === activePlanVersionNumber) {
                return message;
            }
        }
        return undefined;
    })();
    const isInputLocked = false;
    const normalizedFinalizationStatus = String(finalizationStatus || '').toLowerCase();
    const finalizationNotice = normalizedFinalizationStatus === 'queued'
        ? (finalizationStale ? 'PR queue stale, retrying...' : 'PR creation queued...')
        : (normalizedFinalizationStatus === 'running'
            ? 'PR creation running...'
            : (normalizedFinalizationStatus === 'failed'
                ? 'PR creation failed. You can retry publishing.'
                : (normalizedFinalizationStatus === 'skipped'
                    ? 'PR creation skipped.'
                    : null)));
    const hasAttachments = instructionFiles.length > 0 || imageFiles.length > 0;
    const totalAttachmentBytes = [...instructionFiles, ...imageFiles].reduce((sum, file) => sum + file.sizeBytes, 0);
    const hiddenClarificationActionValues = new Set([
        'resume_with_context',
        'retry_failed_backlog',
        'retry_step',
        'retry_all_failed'
    ]);
    const normalizedClarificationOptions = Array.isArray(clarificationOptions)
        ? clarificationOptions.filter((option) => {
            const value = String(option?.value || '').trim();
            const actionKind = String((option as any)?.actionKind || '').trim().toLowerCase();
            return !hiddenClarificationActionValues.has(value) && actionKind !== 'discussion';
        })
        : [];
    const normalizedDiscussionActions = Array.isArray(discussionActions)
        ? discussionActions
        : [];
    const clarificationPhaseHint = String(
        clarificationContext?.phase
        || (clarificationContext?.metadata as Record<string, unknown> | undefined)?.phase
        || ''
    ).trim().toLowerCase();
    const currentClarificationOverlayContextId = (
        clarificationContext
        && typeof clarificationContext.id === 'string'
        && clarificationContext.id.trim() !== ''
    )
        ? clarificationContext.id.trim()
        : '';
    const showDismissedClarificationPill =
        !!showClarificationResumePill
        && !!currentClarificationOverlayContextId;
    const hasResumeDecisionToken =
        !!resumeDiscussionAction
        && typeof resumeDiscussionAction.actionId === 'string'
        && resumeDiscussionAction.actionId.trim() !== '';
    const resumeDecisionLabel = hasResumeDecisionToken ? 'Resume decision' : 'Return to decision';
    const clarificationOverlayExpanded = clarificationOverlayView === 'expanded';
    const clarificationOverlayTitle = typeof clarificationContext?.title === 'string' && clarificationContext.title.trim() !== ''
        ? clarificationContext.title.trim()
        : 'Awaiting decision';
    const clarificationOverlayMetadata =
        clarificationContext && typeof clarificationContext.metadata === 'object' && clarificationContext.metadata
            ? clarificationContext.metadata as Record<string, unknown>
            : null;
    const clarificationOverlayPayload =
        clarificationContext && typeof (clarificationContext as any).payload === 'object' && (clarificationContext as any).payload
            ? (clarificationContext as any).payload as Record<string, unknown>
            : null;
    const clarificationOverlayQuestion = typeof clarificationContext?.question === 'string'
        ? clarificationContext.question.trim()
        : '';
    const clarificationOverlayPhase = clarificationPhaseHint;
    const clarificationOverlayReasonCode = typeof clarificationOverlayMetadata?.reasonCode === 'string'
        ? clarificationOverlayMetadata.reasonCode.trim().toLowerCase()
        : '';
    const compactClarificationOverlayPhases = new Set([
        'execution_pause',
        'post_pr_decision',
        'strict_verification_recovery',
        'next_batch_handoff'
    ]);
    const shouldHideClarificationOverlayQuestion = compactClarificationOverlayPhases.has(clarificationOverlayPhase);
    const derivedClarificationOverlayTitle = (() => {
        if (clarificationOverlayPhase === 'execution_pause') return 'Execution paused';
        if (clarificationOverlayPhase === 'post_pr_decision' || clarificationOverlayPhase === 'next_batch_handoff') {
            return 'Ready for decision';
        }
        if (clarificationOverlayPhase === 'strict_verification_recovery') {
            if (
                clarificationOverlayReasonCode.includes('deferred')
                || clarificationOverlayReasonCode.includes('defer_to_ci')
                || clarificationOverlayReasonCode.includes('bootstrap')
                || /local verification is incomplete|bootstrap|required checks|deferred|setup requires/i.test(clarificationOverlayQuestion)
            ) {
                return 'Local verification incomplete';
            }
            if (
                clarificationOverlayReasonCode.includes('blocked')
                || /could not produce a complete local build verdict|could not validate/i.test(clarificationOverlayQuestion)
            ) {
                return 'Local verification blocked';
            }
            return 'Local verification failed';
        }
        return clarificationOverlayTitle;
    })();
    const hasActionableClarificationOptions = normalizedClarificationOptions.length > 0;
    const normalizeVerificationSummary = useCallback((rawSummary: Record<string, unknown> | null) => {
        if (!rawSummary) {
            return null;
        }
        const normalizeStatus = (value: unknown) => {
            const normalized = String(value || '').trim().toLowerCase();
            return ['passed', 'failed', 'skipped', 'blocked', 'deferred'].includes(normalized) ? normalized : 'skipped';
        };
        const checkEntries = Array.isArray(rawSummary.checks)
            ? rawSummary.checks
                .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
                .map((entry) => ({
                    kind: String(entry.kind || '').trim().toLowerCase(),
                    label: String(entry.label || entry.kind || '').trim(),
                    state: normalizeStatus(entry.state),
                    reason: typeof entry.reason === 'string' && entry.reason.trim() !== ''
                        ? entry.reason.trim()
                        : null,
                    handledBy: typeof entry.handledBy === 'string' && entry.handledBy.trim() !== ''
                        ? entry.handledBy.trim()
                        : null
                }))
            : [
                { kind: 'build', label: 'Build', state: normalizeStatus(rawSummary.build), reason: null, handledBy: null },
                { kind: 'types', label: 'Types', state: normalizeStatus(rawSummary.types), reason: null, handledBy: null },
                { kind: 'syntax', label: 'Syntax', state: normalizeStatus(rawSummary.syntax), reason: null, handledBy: null },
                { kind: 'test', label: 'Tests', state: normalizeStatus(rawSummary.test), reason: null, handledBy: null },
                { kind: 'lint', label: 'Lint', state: normalizeStatus(rawSummary.lint), reason: null, handledBy: null }
            ];
        const entries = checkEntries.map((entry) => [entry.label, entry.state] as [string, string]);
        const blockingSummary = typeof rawSummary.blockingSummary === 'string' && rawSummary.blockingSummary.trim() !== ''
            ? rawSummary.blockingSummary.trim()
            : null;
        return {
            checks: checkEntries,
            entries,
            blockingSummary,
            repairOutcomeKind: typeof rawSummary.repairOutcomeKind === 'string' && rawSummary.repairOutcomeKind.trim() !== ''
                ? rawSummary.repairOutcomeKind.trim()
                : null
        };
    }, []);
    const verificationGateSummary = useMemo(() => {
        const rawSummary = clarificationOverlayPayload && typeof clarificationOverlayPayload.verificationGateSummary === 'object'
            ? clarificationOverlayPayload.verificationGateSummary as Record<string, unknown>
            : null;
        return normalizeVerificationSummary(rawSummary);
    }, [clarificationOverlayPayload, normalizeVerificationSummary]);
    const liveVerificationSummary = useMemo(() => {
        const rawSummary = verificationDisplay?.summary && typeof verificationDisplay.summary === 'object'
            ? verificationDisplay.summary as Record<string, unknown>
            : null;
        return normalizeVerificationSummary(rawSummary);
    }, [verificationDisplay, normalizeVerificationSummary]);
    const liveVerificationPhase = typeof verificationDisplay?.lifecycle === 'string' && verificationDisplay.lifecycle.trim() !== ''
        ? verificationDisplay.lifecycle.trim().toLowerCase()
        : (typeof verificationDisplay?.phase === 'string'
            ? verificationDisplay.phase.trim().toLowerCase()
            : '');
    const liveVerificationGateStatus = typeof verificationDisplay?.gateStatus === 'string'
        ? verificationDisplay.gateStatus.trim().toLowerCase()
        : '';
    const displayedVerificationSummary = verificationGateSummary || liveVerificationSummary;
    const liveVerificationConfidenceLevel = typeof verificationDisplay?.confidenceLevel === 'string'
        ? verificationDisplay.confidenceLevel.trim()
        : '';
    const liveVerificationConfidenceReason = typeof verificationDisplay?.confidenceReason === 'string'
        ? verificationDisplay.confidenceReason.trim()
        : '';
    const formatVerificationStateLabel = useCallback((status: string) => {
        switch (String(status || '').trim().toLowerCase()) {
            case 'passed':
                return 'passed';
            case 'failed':
                return 'failed';
            case 'blocked':
                return 'blocked';
            case 'deferred':
                return 'deferred to CI';
            case 'skipped':
            default:
                return 'not applicable';
        }
    }, []);
    const verificationStateBadgeClass = useCallback((status: string) => {
        switch (String(status || '').trim().toLowerCase()) {
            case 'passed':
                return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
            case 'failed':
                return 'border-rose-500/40 bg-rose-500/10 text-rose-300';
            case 'blocked':
                return 'border-orange-500/40 bg-orange-500/10 text-orange-300';
            case 'deferred':
                return 'border-sky-500/40 bg-sky-500/10 text-sky-300';
            case 'skipped':
            default:
                return 'border-border/70 bg-background text-muted-foreground';
        }
    }, []);
    const renderCompactVerificationStatusCard = useCallback((params: {
        summary: NonNullable<typeof displayedVerificationSummary>;
        gateStatus?: string;
        confidenceLevel?: string;
    }) => {
        const counts = params.summary.checks.reduce<Record<string, number>>((acc, entry) => {
            acc[entry.state] = (acc[entry.state] || 0) + 1;
            return acc;
        }, {});
        const attentionCount = (counts.failed || 0) + (counts.blocked || 0);
        const incompleteCount = counts.deferred || 0;
        const verificationTooltip = (() => {
            const reason = simplifyVerificationCopy(params.summary.blockingSummary);
            if (attentionCount > 0 || incompleteCount > 0) {
                return `Skip verification: ${reason}`;
            }
            return 'Verification passed';
        })();
        const gateStatusLabel = (() => {
            if (attentionCount === 0 && incompleteCount === 0) {
                return 'PR Ready';
            }
            switch (String(params.gateStatus || '').trim().toLowerCase()) {
                case 'pr_ready':
                    return 'PR Ready';
                case 'awaiting_ci':
                    return 'Awaiting CI';
                case 'repair_required':
                    return 'Repair Required';
                case 'unverified_pr':
                    return 'Unverified PR';
                case 'running':
                    return 'Running';
                default:
                    return '';
            }
        })();
        const confidenceLabel = params.confidenceLevel
            ? params.confidenceLevel.charAt(0).toUpperCase() + params.confidenceLevel.slice(1).replace(/_/g, ' ')
            : '';
        const compactCounts = [
            counts.passed ? `${counts.passed} passed` : '',
            counts.failed ? `${counts.failed} failed` : '',
            counts.blocked ? `${counts.blocked} blocked` : '',
            counts.deferred ? `${counts.deferred} deferred` : ''
        ].filter(Boolean);
        const visibleEntries = params.summary.entries
            .filter(([, status]) => String(status || '').trim().toLowerCase() !== 'skipped')
            .slice(0, 5);
        return (
            <div className="rounded-lg bg-card p-3 space-y-3" title={verificationTooltip}>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        {attentionCount > 0 ? (
                            <AlertTriangle className="h-4 w-4 text-rose-400" />
                        ) : incompleteCount > 0 ? (
                            <AlertTriangle className="h-4 w-4 text-amber-400" />
                        ) : (
                            <Check className="h-4 w-4 text-emerald-400" />
                        )}
                        <span className="text-sm font-semibold">Verification</span>
                    </div>
                    {(gateStatusLabel || confidenceLabel) && (
                        <div className="flex shrink-0 items-center gap-2 text-xs">
                            {gateStatusLabel && (
                                <span className="rounded border border-border/70 bg-muted/40 px-2 py-0.5 text-foreground">
                                    {gateStatusLabel}
                                </span>
                            )}
                            {confidenceLabel && (
                                <span className="text-muted-foreground">
                                    Confidence: {confidenceLabel}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {compactCounts.length > 0 && (
                    <div className="flex flex-wrap gap-2 text-xs">
                        {compactCounts.map((entry) => (
                            <span key={entry} className="rounded-md bg-muted/40 px-2 py-1 text-foreground">
                                {entry}
                            </span>
                        ))}
                    </div>
                )}
                {visibleEntries.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {visibleEntries.map(([label, status]) => (
                            <span
                                key={`${label}-${status}`}
                                className={cn(
                                    'rounded-full border px-2 py-1 text-[11px] font-medium',
                                    verificationStateBadgeClass(status)
                                )}
                            >
                                {label} {formatVerificationStateLabel(status)}
                            </span>
                        ))}
                    </div>
                )}
                {params.summary.blockingSummary && (
                    <p className="text-xs text-muted-foreground">
                        {simplifyVerificationCopy(params.summary.blockingSummary)}
                    </p>
                )}
            </div>
        );
    }, [displayedVerificationSummary, formatVerificationStateLabel, verificationStateBadgeClass]);
    const shouldShowInlineLiveVerificationCard =
        !!verificationDisplay
        && ['running', 'repairing', 'rerunning'].includes(liveVerificationPhase);
    const liveVerificationComponent = shouldShowInlineLiveVerificationCard
        ? buildVerificationUpdateComponent({
            verificationDisplay,
            allowActions: false
        })
        : null;
    const hasActiveVerificationSourceMessage = useMemo(() => {
        const sourceMessageId = typeof clarificationContext?.sourceMessageId === 'string'
            ? clarificationContext.sourceMessageId.trim()
            : '';
        if (!sourceMessageId) return false;
        return messages.some((message) => {
            if (message.id !== sourceMessageId) return false;
            if (message.role !== 'assistant' || message.type !== 'clarification') return false;
            const phase = typeof message.metadata?.phase === 'string'
                ? message.metadata.phase.trim().toLowerCase()
                : '';
            return isChatNativeVerificationPhase(phase);
        });
    }, [clarificationContext?.sourceMessageId, messages]);
    const shouldShowInlineTerminalVerificationCard =
        !verificationDisplay?.active
        && !!verificationDisplay
        && (
            clarificationOverlayPhase === 'strict_verification_recovery'
            || ['failed', 'blocked'].includes(liveVerificationPhase)
            || verificationDecisionRequired === true
        );
    const terminalVerificationComponent = shouldShowInlineTerminalVerificationCard
        ? buildVerificationUpdateComponent({
            verificationDisplay,
            clarificationOptions: normalizedClarificationOptions,
            allowActions: true
        })
        : null;
    const shouldRenderComposerClarificationOverlay =
        showClarificationOverlay
        && normalizedClarificationOptions.length > 0;
    const shouldHidePrPreviewForVerificationState =
        clarificationOverlayPhase === 'strict_verification_recovery'
        || verificationDecisionRequired === true
        || ['running', 'failed', 'blocked'].includes(liveVerificationPhase);
    const suppressInlineClarificationUi =
        !!clarificationContext && (shouldRenderComposerClarificationOverlay || isClarificationOverlayDismissed);
    useEffect(() => {
        if (shouldRenderComposerClarificationOverlay && isComposerMenuOpen) {
            setIsComposerMenuOpen(false);
        }
    }, [shouldRenderComposerClarificationOverlay, isComposerMenuOpen]);
    const normalizeClarificationOptionLabel = (option: { value?: string; label?: string }) => {
        const overrideLabel = normalizePrOverrideActionLabel(option.value, '');
        if (overrideLabel) {
            return overrideLabel;
        }
        const baseLabel = typeof option.label === 'string' ? option.label : '';
        if (baseLabel) {
            return baseLabel
                .replace(/\s*\([^)]*\)\s*$/i, '')
                .replace(/\s*[\u2022\u00b7]\s*.*$/i, '')
                .trim();
        }
        return baseLabel;
    };
    const clarificationOverlayChoices = useMemo<ClarificationOverlayChoice[]>(() => {
        if (!hasActionableClarificationOptions) {
            return [];
        }
        const actionChoices = normalizedClarificationOptions.map((option, index) => ({
            key: [
                option.value || 'option',
                option.actionId || 'no-action-token',
                index
            ].join('::'),
            label: normalizeClarificationOptionLabel(option),
            kind: 'action' as const,
            option
        }));
        return [
            ...actionChoices,
            {
                key: CLARIFICATION_OTHER_CHOICE_KEY,
                label: 'No, and tell Swayashin what to do differently',
                kind: 'other'
            }
        ];
    }, [hasActionableClarificationOptions, normalizedClarificationOptions, normalizeClarificationOptionLabel]);
    const clarificationOverlayActionFingerprint = useMemo(() => {
        if (!shouldRenderComposerClarificationOverlay) {
            return '';
        }
        return JSON.stringify({
            contextId: currentClarificationOverlayContextId,
            options: clarificationOverlayChoices.map((choice) => ({
                key: choice.key,
                kind: choice.kind,
                value: choice.option?.value || '',
                actionId: choice.option?.actionId || ''
            }))
        });
    }, [
        clarificationOverlayChoices,
        currentClarificationOverlayContextId,
        shouldRenderComposerClarificationOverlay
    ]);
    const selectedClarificationChoice = clarificationOverlayChoices.find(
        (choice) => choice.key === selectedClarificationChoiceKey
    ) || (
        latestSelectedClarificationChoiceRef.current
        && clarificationOverlayChoices.some((choice) => choice.key === latestSelectedClarificationChoiceRef.current?.key)
            ? latestSelectedClarificationChoiceRef.current
            : null
    );
    const selectClarificationChoice = (choice: ClarificationOverlayChoice) => {
        latestSelectedClarificationChoiceRef.current = choice;
        setSelectedClarificationChoiceKey(choice.key);
        setClarificationSelectionResetReason(null);
    };
    const clarificationOtherChoiceSelected = selectedClarificationChoice?.kind === 'other';
    const selectedOverlayActionValue = selectedClarificationChoice?.option?.value || '';
    const overlayPayload = clarificationSelectionDraft || {};
    const normalizedClarificationBatchOptions = useMemo(() =>
        (Array.isArray(clarificationBatchOptions) ? clarificationBatchOptions : [])
            .filter((entry) => entry && typeof entry.batchId === 'string' && entry.batchId.trim() !== '')
            .map((entry) => ({
                batchId: entry.batchId.trim(),
                label: typeof entry.label === 'string' && entry.label.trim() !== ''
                    ? entry.label.trim()
                    : entry.batchId.trim(),
                latestGoal: typeof entry.latestGoal === 'string' && entry.latestGoal.trim() !== ''
                    ? entry.latestGoal.trim()
                    : null,
                latestGoalLabel: typeof entry.latestGoalLabel === 'string' && entry.latestGoalLabel.trim() !== ''
                    ? entry.latestGoalLabel.trim()
                    : null,
                currentGoal: typeof entry.currentGoal === 'string' && entry.currentGoal.trim() !== ''
                    ? entry.currentGoal.trim()
                    : null,
                currentGoalLabel: typeof entry.currentGoalLabel === 'string' && entry.currentGoalLabel.trim() !== ''
                    ? entry.currentGoalLabel.trim()
                    : null,
                exhaustedGoals: Array.isArray(entry.exhaustedGoals)
                    ? entry.exhaustedGoals
                        .filter((goal): goal is string => typeof goal === 'string' && goal.trim() !== '')
                        .map((goal) => goal.trim())
                    : [],
                goalOptions: Array.isArray(entry.goalOptions)
                    ? entry.goalOptions
                        .filter((goalOption) =>
                            goalOption
                            && typeof goalOption.value === 'string'
                            && goalOption.value.trim() !== ''
                        )
                        .map((goalOption) => ({
                            value: goalOption.value.trim(),
                            label: typeof goalOption.label === 'string' && goalOption.label.trim() !== ''
                                ? goalOption.label.trim()
                                : formatGoalLabel(goalOption.value),
                            disabled: goalOption.disabled === true,
                            disabledReason: typeof goalOption.disabledReason === 'string' && goalOption.disabledReason.trim() !== ''
                                ? goalOption.disabledReason.trim()
                                : undefined
                        }))
                    : []
            })),
        [clarificationBatchOptions]
    );
    const hasMonitoringSelection =
        (typeof overlayPayload.monitoringId === 'string' && overlayPayload.monitoringId.trim() !== '')
        || (typeof overlayPayload.monitoringUrl === 'string' && overlayPayload.monitoringUrl.trim() !== '');
    const requiresMonitoringSelection = selectedOverlayActionValue === 'attach_monitoring_preplan';
    const requiresGoalSelection = selectedOverlayActionValue === 'continue_with_new_goal';
    const selectedBatchId = typeof overlayPayload.selectedBatchId === 'string' && overlayPayload.selectedBatchId.trim() !== ''
        ? overlayPayload.selectedBatchId.trim()
        : typeof selectedClarificationChoice?.option?.selectedBatchId === 'string' && selectedClarificationChoice.option.selectedBatchId.trim() !== ''
            ? selectedClarificationChoice.option.selectedBatchId.trim()
        : (
            typeof activeBatchId === 'string' && activeBatchId.trim() !== ''
                ? activeBatchId.trim()
                : (normalizedClarificationBatchOptions[0]?.batchId || undefined)
        );
    const selectedGoal = (
        overlayPayload.selectedGoal === 'performance'
        || overlayPayload.selectedGoal === 'security'
        || overlayPayload.selectedGoal === 'debugging'
        || overlayPayload.selectedGoal === 'stability'
    )
        ? overlayPayload.selectedGoal
        : undefined;
    const selectedBatchOption = useMemo(
        () => normalizedClarificationBatchOptions.find((entry) => entry.batchId === selectedBatchId) || null,
        [normalizedClarificationBatchOptions, selectedBatchId]
    );
    const selectedBatchCurrentGoal = useMemo(() => {
        if (typeof selectedBatchOption?.currentGoal === 'string' && selectedBatchOption.currentGoal.trim() !== '') {
            return selectedBatchOption.currentGoal.trim();
        }
        if (typeof selectedBatchOption?.latestGoal === 'string' && selectedBatchOption.latestGoal.trim() !== '') {
            return selectedBatchOption.latestGoal.trim();
        }
        return activeGoal;
    }, [activeGoal, selectedBatchOption]);
    const selectedBatchCurrentGoalLabel = useMemo(() => {
        if (typeof selectedBatchOption?.currentGoalLabel === 'string' && selectedBatchOption.currentGoalLabel.trim() !== '') {
            return selectedBatchOption.currentGoalLabel.trim();
        }
        if (typeof selectedBatchOption?.latestGoalLabel === 'string' && selectedBatchOption.latestGoalLabel.trim() !== '') {
            return selectedBatchOption.latestGoalLabel.trim();
        }
        return formatGoalLabel(selectedBatchCurrentGoal);
    }, [selectedBatchCurrentGoal, selectedBatchOption]);
    const selectedBatchHasGoalContinuationState = useMemo(() => {
        if (!selectedBatchOption) {
            return false;
        }
        return (
            (typeof selectedBatchOption.currentGoal === 'string' && selectedBatchOption.currentGoal.trim() !== '')
            || selectedBatchOption.goalOptions.length > 0
            || selectedBatchOption.exhaustedGoals.length > 0
        );
    }, [selectedBatchOption]);
    const selectedBatchGoalOptionByValue = useMemo(() => new Map(
        (selectedBatchOption?.goalOptions || []).map((goalOption) => [goalOption.value, goalOption])
    ), [selectedBatchOption]);
    const selectedBatchExhaustedGoals = useMemo(
        () => new Set(selectedBatchOption?.exhaustedGoals || []),
        [selectedBatchOption]
    );
    const selectedBatchUsedGoals = useMemo(
        () => new Set(selectedBatchOption?.usedGoals || []),
        [selectedBatchOption]
    );
    const selectedGoalMatchesCurrent = !!selectedGoal && selectedGoal === selectedBatchCurrentGoal;
    const requiresConcreteBatchForGoalSelection =
        selectedBatchHasGoalContinuationState || normalizedClarificationBatchOptions.length > 0;
    const missingRequiredOverlaySelection =
        (requiresMonitoringSelection && !hasMonitoringSelection)
        || (requiresGoalSelection && (
            !selectedGoal
            || selectedGoalMatchesCurrent
            || (requiresConcreteBatchForGoalSelection && !selectedBatchId)
        ));
    const clarificationSubmitDisabled = isClarificationLoading
        || !selectedClarificationChoice
        || (
            clarificationOtherChoiceSelected
                ? clarificationInputValue.trim().length === 0 || !onClarificationDiscussSubmit
                : clarificationForceDisabled
                || !selectedClarificationChoice?.option
                || selectedClarificationChoice?.option?.disabled === true
                || missingRequiredOverlaySelection
        );
    const buildClarificationSubmitPayload = (selectedOption: ClarificationActionOptionValue) => {
        const monitoringId = typeof overlayPayload.monitoringId === 'string' && overlayPayload.monitoringId.trim() !== ''
            ? overlayPayload.monitoringId.trim()
            : undefined;
        const monitoringUrl = typeof overlayPayload.monitoringUrl === 'string' && overlayPayload.monitoringUrl.trim() !== ''
            ? overlayPayload.monitoringUrl.trim()
            : undefined;
        return {
            value: selectedOption.value,
            label: selectedOption.label,
            actionId: selectedOption.actionId,
            expiresAt: selectedOption.expiresAt,
            variant: selectedOption.variant,
            ...(Number.isFinite(Number(selectedOption.selectedPlanVersion ?? selectedOption.planVersion)) &&
            Number(selectedOption.selectedPlanVersion ?? selectedOption.planVersion) > 0
                ? {
                    selectedPlanVersion: Math.floor(Number(selectedOption.selectedPlanVersion ?? selectedOption.planVersion))
                }
                : {}),
            ...(selectedOption.value === 'continue_with_new_goal' && selectedGoal ? { selectedGoal } : {}),
            ...(
                (
                    selectedOption.value === 'continue_with_new_goal'
                    || selectedOption.value === 'find_more_issues'
                ) && selectedBatchId
                    ? { selectedBatchId }
                    : {}
            ),
            ...(monitoringId ? { monitoringId } : {}),
            ...(monitoringUrl ? { monitoringUrl } : {})
        };
    };
    const submitSpecificClarificationAction = (selectedOption: ClarificationActionOptionValue) => {
        if (selectedOption.disabled) {
            toast.info(selectedOption.disabledReason || 'This action is currently unavailable.');
            return;
        }
        const submitPayload = buildClarificationSubmitPayload(selectedOption);
        const currentContextId = currentClarificationOverlayContextId || '';
        const actionKey = [
            currentContextId,
            submitPayload.actionId || '',
            submitPayload.value || selectedOption.value || '',
            submitPayload.selectedGoal || '',
            submitPayload.selectedBatchId || ''
        ].join(':');
        const existingGuard = clarificationSubmitGuardRef.current;
        if (
            existingGuard
            && existingGuard.contextId === currentContextId
            && existingGuard.actionKey === actionKey
        ) {
            return;
        }
        clarificationSubmitGuardRef.current = {
            contextId: currentContextId,
            actionKey,
            submittedAt: Date.now()
        };
        onClarificationSubmit?.(submitPayload);
    };
    const submitClarificationOverlayChoice = () => {
        const currentSelectedChoice = selectedClarificationChoice
            || (
                latestSelectedClarificationChoiceRef.current
                && clarificationOverlayChoices.some((choice) => choice.key === latestSelectedClarificationChoiceRef.current?.key)
                    ? latestSelectedClarificationChoiceRef.current
                    : null
            );
        if (clarificationSubmitDisabled || !currentSelectedChoice) return;
        const currentContextId = currentClarificationOverlayContextId || '';
        if (currentSelectedChoice.kind === 'other') {
            const text = clarificationInputValue.trim();
            if (!text) return;
            if (!onClarificationDiscussSubmit) return;
            const actionKey = `${currentContextId}:other:${text.toLowerCase()}`;
            const existingGuard = clarificationSubmitGuardRef.current;
            if (
                existingGuard
                && existingGuard.contextId === currentContextId
                && existingGuard.actionKey === actionKey
            ) {
                return;
            }
            clarificationSubmitGuardRef.current = {
                contextId: currentContextId,
                actionKey,
                submittedAt: Date.now()
            };
            if (currentClarificationOverlayContextId) {
                onClarificationResumeCue?.({
                    contextId: currentClarificationOverlayContextId,
                    submittedAt: new Date().toISOString()
                });
            }
            onClarificationDiscussSubmit(text);
            setClarificationInputValue('');
            return;
        }
        const selectedOption = currentSelectedChoice.option;
        if (!selectedOption) return;
        submitSpecificClarificationAction(selectedOption);
    };

    useEffect(() => {
        if (!shouldRenderComposerClarificationOverlay) return;
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            const isTypingTarget = !!target && (
                target.tagName === 'INPUT'
                || target.tagName === 'TEXTAREA'
                || target.isContentEditable
            );
            const isButtonTarget = !!target && (
                target.tagName === 'BUTTON'
                || !!target.closest('button')
            );
            if (event.key === 'Escape') {
                onDismissClarificationOverlay?.();
                return;
            }
            if (event.key === 'Tab') {
                const panel = clarificationOverlayRef.current;
                if (!panel) return;
                const focusables = Array.from(
                    panel.querySelectorAll<HTMLElement>(
                        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    )
                );
                if (focusables.length === 0) {
                    event.preventDefault();
                    return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const activeElement = document.activeElement as HTMLElement | null;
                const activeInside = !!activeElement && panel.contains(activeElement);
                if (!activeInside) {
                    event.preventDefault();
                    first.focus();
                    return;
                }
                if (event.shiftKey && activeElement === first) {
                    event.preventDefault();
                    last.focus();
                    return;
                }
                if (!event.shiftKey && activeElement === last) {
                    event.preventDefault();
                    first.focus();
                    return;
                }
                return;
            }
            if (isTypingTarget && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                return;
            }
            if (!clarificationOverlayChoices.length) return;
            const currentIndex = clarificationOverlayChoices.findIndex(
                (choice) => choice.key === selectedClarificationChoice?.key
            );
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                const nextIndex = currentIndex < clarificationOverlayChoices.length - 1
                    ? currentIndex + 1
                    : clarificationOverlayChoices.length - 1;
                selectClarificationChoice(clarificationOverlayChoices[nextIndex]);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                const nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;
                selectClarificationChoice(clarificationOverlayChoices[nextIndex]);
                return;
            }
            if (event.key === 'Enter' && !event.shiftKey) {
                if (isButtonTarget || clarificationSubmitGuardRef.current) {
                    return;
                }
                event.preventDefault();
                if (!clarificationSubmitDisabled) {
                    submitClarificationOverlayChoice();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [
        clarificationOverlayChoices,
        clarificationSubmitDisabled,
        onClarificationSubmit,
        onDismissClarificationOverlay,
        selectedClarificationChoice,
        submitClarificationOverlayChoice,
        shouldRenderComposerClarificationOverlay
    ]);

    useEffect(() => {
        if (!isClarificationLoading) {
            clarificationSubmitGuardRef.current = null;
        }
    }, [
        isClarificationLoading,
        clarificationOverlayActionFingerprint,
        currentClarificationOverlayContextId
    ]);

    useEffect(() => {
        if (!shouldRenderComposerClarificationOverlay) return;
        const panel = clarificationOverlayRef.current;
        if (!panel) return;
        const firstFocusable = panel.querySelector<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
    }, [shouldRenderComposerClarificationOverlay, clarificationOverlayView, currentClarificationOverlayContextId]);

    useEffect(() => {
        if (!shouldRenderComposerClarificationOverlay) {
            setClarificationInputValue('');
            setSelectedClarificationChoiceKey('');
            setClarificationSelectionResetReason(null);
            latestSelectedClarificationChoiceRef.current = null;
            lastClarificationOverlayFingerprintRef.current = '';
            return;
        }
        const previousFingerprint = lastClarificationOverlayFingerprintRef.current;
        if (!previousFingerprint) {
            lastClarificationOverlayFingerprintRef.current = clarificationOverlayActionFingerprint;
            if (
                selectedClarificationChoiceKey
                && !clarificationOverlayChoices.some((choice) => choice.key === selectedClarificationChoiceKey)
            ) {
                latestSelectedClarificationChoiceRef.current = null;
                setSelectedClarificationChoiceKey('');
                setClarificationSelectionResetReason('options_updated');
            }
            return;
        }
        if (previousFingerprint !== clarificationOverlayActionFingerprint) {
            lastClarificationOverlayFingerprintRef.current = clarificationOverlayActionFingerprint;
            latestSelectedClarificationChoiceRef.current = null;
            setSelectedClarificationChoiceKey('');
            setClarificationSelectionResetReason('options_updated');
            return;
        }
        if (
            selectedClarificationChoiceKey
            && clarificationOverlayChoices.length > 0
            && !clarificationOverlayChoices.some((choice) => choice.key === selectedClarificationChoiceKey)
        ) {
            latestSelectedClarificationChoiceRef.current = null;
            setSelectedClarificationChoiceKey('');
            setClarificationSelectionResetReason('options_updated');
        }
    }, [
        clarificationOverlayActionFingerprint,
        clarificationOverlayChoices,
        selectedClarificationChoiceKey,
        shouldRenderComposerClarificationOverlay
    ]);

    useEffect(() => {
        if (!currentClarificationOverlayContextId) {
            lastClarificationOverlayContextIdRef.current = '';
            setClarificationOverlayView('expanded');
            return;
        }
        if (lastClarificationOverlayContextIdRef.current !== currentClarificationOverlayContextId) {
            lastClarificationOverlayContextIdRef.current = currentClarificationOverlayContextId;
            setClarificationOverlayView('expanded');
        }
    }, [currentClarificationOverlayContextId]);

    const clearAttachments = () => {
        setInstructionFiles([]);
        setImageFiles([]);
        if (attachmentInputRef.current) {
            attachmentInputRef.current.value = '';
        }
    };

    const removeInstructionFile = (name: string) => {
        setInstructionFiles((prev) => prev.filter((file) => file.name !== name));
    };

    const removeImageFile = (name: string) => {
        setImageFiles((prev) => prev.filter((file) => file.name !== name));
    };

    const appendAttachments = async (selected: File[]) => {
        if (selected.length === 0) return;
        const nextInstructionFiles = [...instructionFiles];
        const nextImageFiles = [...imageFiles];
        let nextTotalBytes = [...instructionFiles, ...imageFiles].reduce((sum, file) => sum + file.sizeBytes, 0);
        const validationErrors: string[] = [];

        for (const [index, file] of selected.entries()) {
            const normalizedFileName = normalizeIncomingFileName(file, index);
            const extension = getExtension(normalizedFileName);
            const mimeType = (file.type || '').toLowerCase();
            const sizeBytes = file.size;

            const isInstruction = ALLOWED_TEXT_EXTENSIONS.has(extension);
            const isImage = ALLOWED_IMAGE_EXTENSIONS.has(extension);

            if (!isInstruction && !isImage) {
                validationErrors.push(`${normalizedFileName}: unsupported file type`);
                continue;
            }

            if (isInstruction) {
                if (!ALLOWED_TEXT_MIME_TYPES.has(mimeType)) {
                    validationErrors.push(`${normalizedFileName}: invalid context file MIME type`);
                    continue;
                }
                if (sizeBytes > MAX_TEXT_FILE_BYTES) {
                    validationErrors.push(`${normalizedFileName}: exceeds ${formatBytes(MAX_TEXT_FILE_BYTES)}`);
                    continue;
                }
                if (nextInstructionFiles.length >= MAX_TEXT_ATTACHMENTS) {
                    validationErrors.push(`Context file limit reached (${MAX_TEXT_ATTACHMENTS})`);
                    continue;
                }
                if (nextInstructionFiles.some((existing) => existing.name === normalizedFileName)) {
                    validationErrors.push(`${normalizedFileName}: duplicate file name`);
                    continue;
                }

                const content = await file.text();
                if (content.includes('\u0000')) {
                    validationErrors.push(`${normalizedFileName}: binary content detected`);
                    continue;
                }

                if (nextTotalBytes + sizeBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
                    validationErrors.push(`Total attachment size exceeds ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}`);
                    continue;
                }

                nextInstructionFiles.push({
                    name: normalizedFileName,
                    mimeType: mimeType || 'text/plain',
                    sizeBytes,
                    content,
                });
                nextTotalBytes += sizeBytes;
                continue;
            }

            if (!ALLOWED_IMAGE_MIME_TYPES.has(mimeType)) {
                validationErrors.push(`${normalizedFileName}: invalid image MIME type`);
                continue;
            }
            const expectedImageMimes = EXPECTED_IMAGE_MIME_BY_EXTENSION[extension];
            if (!expectedImageMimes || !expectedImageMimes.has(mimeType)) {
                validationErrors.push(`${normalizedFileName}: extension and MIME type do not match`);
                continue;
            }
            if (sizeBytes > MAX_IMAGE_FILE_BYTES) {
                validationErrors.push(`${normalizedFileName}: exceeds ${formatBytes(MAX_IMAGE_FILE_BYTES)}`);
                continue;
            }
            if (nextImageFiles.length >= MAX_IMAGE_ATTACHMENTS) {
                validationErrors.push(`Image limit reached (${MAX_IMAGE_ATTACHMENTS})`);
                continue;
            }
            if (nextImageFiles.some((existing) => existing.name === normalizedFileName)) {
                validationErrors.push(`${normalizedFileName}: duplicate file name`);
                continue;
            }
            if (nextTotalBytes + sizeBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
                validationErrors.push(`Total attachment size exceeds ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}`);
                continue;
            }

            try {
                const dataUrl = await readFileAsDataUrl(file);
                if (!dataUrl.startsWith(`data:${mimeType};base64,`)) {
                    validationErrors.push(`${normalizedFileName}: malformed image payload`);
                    continue;
                }
                nextImageFiles.push({
                    name: normalizedFileName,
                    mimeType: mimeType || 'image/png',
                    sizeBytes,
                    dataUrl,
                });
                nextTotalBytes += sizeBytes;
            } catch {
                validationErrors.push(`${normalizedFileName}: failed to read image file`);
            }
        }

        setInstructionFiles(nextInstructionFiles);
        setImageFiles(nextImageFiles);

        if (validationErrors.length > 0) {
            toast.error(`Some attachments were rejected: ${validationErrors.slice(0, 2).join('; ')}`);
        }

        if (attachmentInputRef.current) {
            attachmentInputRef.current.value = '';
        }
    };

    const handleAttachmentSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(event.target.files || []);
        await appendAttachments(selected);
    };

    const handlePaste = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardFiles = Array.from(event.clipboardData?.files || []);
        const clipboardImages = clipboardFiles.filter((file) => file.type.toLowerCase().startsWith('image/'));
        if (clipboardImages.length === 0) return;
        event.preventDefault();
        await appendAttachments(clipboardImages);
    };

    const handleSend = async () => {
        console.log('[AgentChatPanel] handleSend called', {
            inputValue: inputValue.trim(),
            hasOnSendMessage: !!onSendMessage,
            canSendMessage,
            isSending,
            isInputLocked,
            isInDiscussionMode,
            hasAttachments,
            attachmentBytes: totalAttachmentBytes,
        });
        if ((inputValue.trim() || hasAttachments) && onSendMessage) {
            const messageToSend = inputValue.trim() || 'Use the attached context files and images for this request.';
            const promptAttachments: PromptAttachments | undefined = hasAttachments
                ? {
                    ...(instructionFiles.length > 0 ? { instructionFiles } : {}),
                    ...(imageFiles.length > 0 ? { images: imageFiles } : {}),
                }
                : undefined;
            console.log('[AgentChatPanel] Calling onSendMessage with:', {
                messageLength: messageToSend.length,
                instructionFileCount: promptAttachments?.instructionFiles?.length || 0,
                imageCount: promptAttachments?.images?.length || 0,
            });
            await Promise.resolve(onSendMessage(messageToSend, { promptAttachments }));
            setInputValue('');
            clearAttachments();
        } else {
            console.log('[AgentChatPanel] NOT calling onSendMessage - conditions not met');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
    };

    // removed early return null

    return (
        <div
            className={cn("relative flex flex-col h-full max-h-full overflow-hidden min-h-0 bg-background", className)}
            style={{ height: '100%' }}
        >
            <div
                ref={scrollRef}
                className="flex-1 px-2 py-2 overflow-y-auto min-h-0 no-scrollbar"
            >
                <div className="space-y-4">
                    {messages
                        .filter(msg => {
                            const isStatus = msg.role === 'system' && (msg.type === 'status' || msg.type === 'progress');
                            return !isStatus;
                        })
                        .map((message) => (
                            <ChatMessage
                                key={message.id}
                                message={message}
                                currentStatus={currentStatus}
                                approvalTransitionInFlight={approvalTransitionInFlight}
                                activePlanVersion={activePlanVersion}
                                activeBatchId={activeBatchId}
                                clarificationSourceMessageId={clarificationContext?.sourceMessageId || null}
                                clarificationContext={clarificationContext}
                                onClarificationSubmit={onClarificationSubmit}
                                onClarificationSelectionChange={onClarificationSelectionChange}
                                activeClarificationActionIds={activeClarificationActionIds}
                                activeClarificationActionValues={activeClarificationActionValues}
                                isClarificationLoading={isClarificationLoading}
                                loadingClarificationActionId={loadingClarificationActionId}
                                clarificationForceDisabled={clarificationForceDisabled}
                                clarificationDisabledReason={clarificationDisabledReason}
                                suppressActiveClarificationUi={suppressInlineClarificationUi}
                                verificationDisplay={verificationDisplay}
                                clarificationOptions={normalizedClarificationOptions}
                                onStepFocusChange={handlePlanStepFocusChange}
                            />
                        ))}

                    {liveVerificationComponent && (
                        <div className="mb-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                                    <Bot className="h-4 w-4 text-purple-600" />
                                </div>
                                <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Agent</span>
                                <span className="text-xs text-muted-foreground ml-auto">Now</span>
                            </div>
                            <div className="pl-0">
                                <JsonRenderMessage
                                    components={[liveVerificationComponent]}
                                />
                            </div>
                        </div>
                    )}
                    {terminalVerificationComponent && !hasActiveVerificationSourceMessage && !shouldRenderComposerClarificationOverlay && (
                        <div className="mb-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                                    <Bot className="h-4 w-4 text-purple-600" />
                                </div>
                                <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Agent</span>
                                <span className="text-xs text-muted-foreground ml-auto">Now</span>
                            </div>
                            <div className="pl-0">
                                <JsonRenderMessage
                                    components={[terminalVerificationComponent]}
                                    onClarificationSubmit={onClarificationSubmit}
                                    onClarificationSelectionChange={onClarificationSelectionChange}
                                    activeClarificationActionIds={activeClarificationActionIds}
                                    activeClarificationActionValues={activeClarificationActionValues}
                                    isClarificationLoading={isClarificationLoading}
                                    loadingClarificationActionId={loadingClarificationActionId}
                                    clarificationForceDisabled={clarificationForceDisabled}
                                    clarificationDisabledReason={clarificationDisabledReason}
                                    suppressActiveClarificationUi={suppressInlineClarificationUi}
                                />
                            </div>
                        </div>
                    )}

                    {/* Pending Plan (at the bottom of chat) - Only render if we haven't shown it in the stream */}
                    {((plan && phases.length > 0) &&
                        !pendingPlanMessage &&
                        ['pending_approval', 'generating_plan', 'analyzing', 'cloning'].includes(
                            String(currentStatus || plan?.status || '').trim().toLowerCase()
                        )) && (
                        <div className="mt-4 mb-2">
                            <PlanAccordion
                                planId={plan?.id || 'plan-1'}
                                title={activePlanTitle}
                                phases={phases as any}
                                status={(plan?.status || 'pending') as any}
                                showApprovalButton={false}
                                // Note: Legacy plan prop rendering doesn't easily support cancel without a message ID
                                // but typically the plan message is in the stream now.
                                approvalTransitionInFlight={approvalTransitionInFlight}
                                onStepFocusChange={handlePlanStepFocusChange}
                            />
                        </div>
                    )}



                    {/* Pending user message - shown while streaming */}
                    {pendingUserMessage && (isStreaming || streamingMessage) && (
                        <div className="mb-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="h-4 w-4 text-primary" />
                                </div>
                                <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">You</span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div className="pl-0">
                                <p className="text-sm whitespace-pre-wrap break-words">{pendingUserMessage}</p>
                            </div>
                        </div>
                    )}

                    {/* Streaming response - matches ChatMessage styling exactly */}
                    {(isStreaming || streamingMessage) && (
                        <div className="mb-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                                    <Bot className="h-4 w-4 text-purple-600" />
                                </div>
                                <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Agent</span>
                                {isStreaming && (
                                    <span className="text-xs text-purple-600 flex items-center gap-1 ml-2">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        {streamingMessage ? 'Typing...' : 'Thinking...'}
                                    </span>
                                )}
                            </div>
                            <div className="pl-0">
                                {streamingMessage ? (
                                    <JsonRenderMessage
                                        components={[{
                                            type: 'markdown',
                                            id: `stream-md-${Date.now()}`,
                                            props: { content: streamingMessage }
                                        }]}
                                        onClarificationSubmit={onClarificationSubmit}
                                        onClarificationSelectionChange={onClarificationSelectionChange}
                                        activeClarificationActionIds={activeClarificationActionIds}
                                        activeClarificationActionValues={activeClarificationActionValues}
                                        isClarificationLoading={isClarificationLoading}
                                        loadingClarificationActionId={loadingClarificationActionId}
                                        clarificationForceDisabled={clarificationForceDisabled}
                                        clarificationDisabledReason={clarificationDisabledReason}
                                        suppressActiveClarificationUi={suppressInlineClarificationUi}
                                    />
                                ) : (isStreaming && streamingComponents.length === 0) && (
                                    <p className="text-sm text-muted-foreground italic">Preparing response...</p>
                                )}
                                {/* Render JSON-render components from stream */}
                                {streamingComponents.length > 0 && (
                                    <div className="mt-3">
                                        <JsonRenderMessage
                                            components={streamingComponents as any}
                                            fallbackMarkdown={streamingMessage || undefined}
                                            onClarificationSubmit={onClarificationSubmit}
                                            onClarificationSelectionChange={onClarificationSelectionChange}
                                            activeClarificationActionIds={activeClarificationActionIds}
                                            activeClarificationActionValues={activeClarificationActionValues}
                                            isClarificationLoading={isClarificationLoading}
                                            loadingClarificationActionId={loadingClarificationActionId}
                                            clarificationForceDisabled={clarificationForceDisabled}
                                            clarificationDisabledReason={clarificationDisabledReason}
                                            suppressActiveClarificationUi={suppressInlineClarificationUi}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isLoading && !isStreaming && (
                        <div className="flex items-center gap-2 text-muted-foreground py-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">Working...</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Post-execution PR preview/publish */}
            {finalizationNotice && (
                <div className="px-3 pt-2 text-xs text-muted-foreground">
                    {finalizationNotice}
                </div>
            )}
            {/* Chat Input - always shown if handler exists */}
            {onSendMessage && (
                <div className="bg-background shrink-0 z-10 p-2">
                    {showDismissedClarificationPill && (
                        <div className="mb-2 flex justify-end">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 rounded-full px-3 text-xs"
                                onClick={() => {
                                    if (hasResumeDecisionToken && resumeDiscussionAction && onDiscussionActionSubmit) {
                                        onDiscussionActionSubmit(resumeDiscussionAction);
                                        return;
                                    }
                                    onResumeClarificationOverlay?.();
                                }}
                                disabled={isDiscussionActionLoading}
                            >
                                {resumeDecisionLabel}
                            </Button>
                        </div>
                    )}
                    <input
                        ref={attachmentInputRef}
                        type="file"
                        className="hidden"
                        multiple
                        accept=".txt,.md,.log,image/png,image/jpeg,image/jpg,image/webp,image/gif,image/bmp,image/svg+xml"
                        onChange={(event) => {
                            void handleAttachmentSelect(event);
                        }}
                    />
                    <div className="rounded-2xl border border-border/70 bg-muted/30">
                        <div className="flex flex-col gap-2">
                            {approvalDecisionRefreshPending && (
                                <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                                    Refreshing decision options...
                                </div>
                            )}
                            {!approvalDecisionRefreshPending && clarificationLoadingStatusText && (
                                <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                                    <span className="inline-flex items-center gap-2">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        {clarificationLoadingStatusText}
                                    </span>
                                </div>
                            )}
                            {!shouldRenderComposerClarificationOverlay && hasAttachments && (
                                <div className="flex flex-wrap gap-1.5 rounded-md border border-border/70 bg-muted/20 p-2">
                                    {instructionFiles.map((file) => (
                                        <span
                                            key={`instruction-${file.name}`}
                                            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[11px]"
                                        >
                                            <FileText className="h-3 w-3 text-muted-foreground" />
                                            <span className="max-w-[180px] truncate">{file.name}</span>
                                            <span className="text-muted-foreground">{formatBytes(file.sizeBytes)}</span>
                                            <button
                                                type="button"
                                                className="rounded p-0.5 hover:bg-muted"
                                                onClick={() => removeInstructionFile(file.name)}
                                                aria-label={`Remove ${file.name}`}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                    {imageFiles.map((file) => (
                                        <span
                                            key={`image-${file.name}`}
                                            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[11px]"
                                        >
                                            <ImageIcon className="h-3 w-3 text-muted-foreground" />
                                            <span className="max-w-[180px] truncate">{file.name}</span>
                                            <span className="text-muted-foreground">{formatBytes(file.sizeBytes)}</span>
                                            <button
                                                type="button"
                                                className="rounded p-0.5 hover:bg-muted"
                                                onClick={() => removeImageFile(file.name)}
                                                aria-label={`Remove ${file.name}`}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="relative">
                                <Textarea
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    onPaste={(event) => {
                                        void handlePaste(event);
                                    }}
                                    placeholder={
                                        canSendMessage
                                            ? inputPlaceholder
                                            : "Agent is working..."
                                    }
                                    disabled={!canSendMessage || isSending || shouldRenderComposerClarificationOverlay}
                                    className="flex-1 text-sm min-h-[88px] max-h-[260px] resize-y border-0 bg-transparent pb-12 pl-4 pr-12 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                    rows={3}
                                />
                                <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                                    <div ref={composerMenuRef} className="relative">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="h-8 w-8 rounded-full p-0 text-muted-foreground hover:text-foreground hover:bg-background/60"
                                            onClick={() => setIsComposerMenuOpen((prev) => !prev)}
                                            disabled={isSending || isLoading || !canSendMessage || shouldRenderComposerClarificationOverlay}
                                            aria-label="Add files"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                        {isComposerMenuOpen && (
                                            <div className="absolute bottom-10 left-0 z-20 w-72 overflow-hidden rounded-xl border border-border bg-background/95 p-2 shadow-xl backdrop-blur-sm">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    className="h-auto w-full justify-start gap-2 rounded-lg px-2.5 py-2 text-xs font-medium"
                                                    onClick={() => {
                                                        attachmentInputRef.current?.click();
                                                        setIsComposerMenuOpen(false);
                                                    }}
                                                    disabled={isSending || isLoading || !canSendMessage || shouldRenderComposerClarificationOverlay}
                                                >
                                                    <Paperclip className="h-3.5 w-3.5" />
                                                    Add images & context files
                                                </Button>
                                                <div className="mt-1 border-t border-border/60 pt-1">
                                                    <ChatLimitsWidget
                                                        compact
                                                        panelClassName="w-full"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant={isLoading && onCancelTask ? "destructive" : "default"}
                                    onClick={() => {
                                        console.log('[AgentChatPanel] Button clicked', {
                                            inputValue: inputValue.trim(),
                                            hasOnSendMessage: !!onSendMessage,
                                            canSendMessage,
                                            isSending,
                                            isInputLocked,
                                            isInDiscussionMode,
                                            isLoading,
                                            hasOnCancelTask: !!onCancelTask,
                                            willCallCancel: isLoading && onCancelTask
                                        });
                                        if (isLoading && onCancelTask) {
                                            onCancelTask();
                                        } else {
                                            void handleSend();
                                        }
                                    }}
                                    disabled={!isLoading && (!inputValue.trim() && !hasAttachments || isSending || !canSendMessage || shouldRenderComposerClarificationOverlay)}
                                    className="absolute bottom-2 right-2 h-8 w-8 rounded-full p-0"
                                >
                                    {isLoading && onCancelTask ? (
                                        <XCircle className="h-4 w-4" />
                                    ) : isSending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {onSendMessage && shouldRenderComposerClarificationOverlay && clarificationContext && (
                <div className="pointer-events-none absolute inset-x-3 bottom-3 z-30">
                    <div
                        ref={clarificationOverlayRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Clarification decision"
                        className={cn(
                            'pointer-events-auto overflow-hidden rounded-2xl border border-border/70 bg-background/95 shadow-xl backdrop-blur-sm transition-[max-height] duration-200 ease-out',
                            clarificationOverlayExpanded
                                ? 'max-h-[70vh]'
                                : 'min-h-[88px] max-h-[88px]'
                        )}
                    >
                        <div className="flex flex-col">
                            <div className="flex items-start justify-between gap-2 border-b border-border/60 px-3 py-2">
                                <p
                                    className={cn(
                                        'text-sm font-medium leading-relaxed',
                                        !clarificationOverlayExpanded && 'line-clamp-1'
                                    )}
                                >
                                    {derivedClarificationOverlayTitle}
                                </p>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setClarificationOverlayView((prev) => prev === 'expanded' ? 'normal' : 'expanded')}
                                    className="h-7 w-7 p-0"
                                    aria-label={clarificationOverlayExpanded ? 'Switch to normal view' : 'Expand clarification overlay'}
                                    title={clarificationOverlayExpanded ? 'Normal view' : 'Expanded view'}
                                >
                                    {clarificationOverlayExpanded ? (
                                        <Minimize2 className="h-3.5 w-3.5" />
                                    ) : (
                                        <Maximize2 className="h-3.5 w-3.5" />
                                    )}
                                </Button>
                            </div>
                            {clarificationOverlayExpanded && (
                                <div className={cn(
                                    'overflow-y-auto no-scrollbar px-3 py-3',
                                    'max-h-[58vh]'
                                )}>
                                    {!shouldHideClarificationOverlayQuestion && clarificationOverlayQuestion && (
                                        <div className="mb-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                                            <MarkdownRenderer
                                                content={clarificationOverlayQuestion}
                                                className="prose prose-sm max-w-none prose-p:mb-2 prose-p:text-sm prose-li:text-sm prose-ul:my-2 prose-strong:text-foreground"
                                            />
                                        </div>
                                    )}
                                    {displayedVerificationSummary && (
                                        <div className="mb-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                                            {renderCompactVerificationStatusCard({
                                                summary: displayedVerificationSummary,
                                                gateStatus: liveVerificationGateStatus,
                                                confidenceLevel: liveVerificationConfidenceLevel
                                            })}
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                            {clarificationOverlayChoices.map((choice, index) => {
                                                const selected = selectedClarificationChoice?.key === choice.key;
                                                const optionDisabled = choice.kind === 'action'
                                                    && choice.option?.disabled === true;
                                                const choiceDisabled = isClarificationLoading || optionDisabled;
                                                if (choice.kind === 'other') {
                                                    return (
                                                        <div
                                                            key={choice.key}
                                                            data-selected={selected ? 'true' : 'false'}
                                                            className={cn(
                                                                'w-full rounded-md border px-3 py-2 text-left text-sm transition-colors focus-within:ring-2 focus-within:ring-ring/60 focus-within:ring-offset-0',
                                                                selected
                                                                    ? 'border-primary bg-primary/10 text-foreground'
                                                                    : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted/55'
                                                            )}
                                                            onClick={() => {
                                                                selectClarificationChoice(choice);
                                                            }}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className="w-4 shrink-0 font-medium">{index + 1}.</span>
                                                                <input
                                                                    id="clarification-input"
                                                                    name="clarificationInput"
                                                                    type="text"
                                                                    value={clarificationInputValue}
                                                                    onFocus={() => {
                                                                        selectClarificationChoice(choice);
                                                                    }}
                                                                    onChange={(event) => {
                                                                        selectClarificationChoice(choice);
                                                                        setClarificationInputValue(event.target.value);
                                                                    }}
                                                                    placeholder="Let's do it differently"
                                                                    disabled={isClarificationLoading || !onClarificationDiscussSubmit}
                                                                    className="w-full border-0 bg-transparent p-0 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-0"
                                                                    aria-label="Tell Swayashin what to do differently"
                                                                />
                                                                <div className="ml-auto flex shrink-0 items-center gap-2">
                                                                    {onDismissClarificationOverlay && (
                                                                        <Button
                                                                            type="button"
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            onClick={onDismissClarificationOverlay}
                                                                        >
                                                                            Dismiss
                                                                            <span className="ml-1 rounded border border-border px-1 text-[10px] leading-4 text-muted-foreground">ESC</span>
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        onClick={submitClarificationOverlayChoice}
                                                                        disabled={clarificationSubmitDisabled}
                                                                    >
                                                                        {isClarificationLoading ? (
                                                                            <>
                                                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                                                Submitting...
                                                                            </>
                                                                        ) : (
                                                                            'Submit'
                                                                        )}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                const isVerificationOverride = isVerificationOverrideOptionValue(choice.option?.value);
                                                const verificationOverrideReason = `Skip verification: ${simplifyVerificationCopy(
                                                    choice.option?.disabledReason
                                                    || liveVerificationConfidenceReason
                                                )}`;
                                                return (
                                                    <button
                                                        key={choice.key}
                                                        type="button"
                                                        disabled={choiceDisabled}
                                                        aria-pressed={selected}
                                                        data-selected={selected ? 'true' : 'false'}
                                                        onClick={() => {
                                                            if (choiceDisabled) return;
                                                            selectClarificationChoice(choice);
                                                        }}
                                                        title={optionDisabled
                                                            ? (choice.option?.disabledReason || 'This action is currently unavailable.')
                                                            : (isVerificationOverride ? verificationOverrideReason : undefined)}
                                                        className={cn(
                                                            'w-full rounded-md border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-0',
                                                            selected
                                                                ? 'border-primary bg-primary/10 text-foreground'
                                                                : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted/55',
                                                            choiceDisabled && 'opacity-60 cursor-not-allowed'
                                                        )}
                                                    >
                                                        <span className="inline-flex w-full items-center gap-2">
                                                            <span className="w-4 shrink-0 font-medium">{index + 1}.</span>
                                                            <span>{choice.label}</span>
                                                            {isVerificationOverride && (
                                                                <AlertTriangle
                                                                    className="ml-auto h-3.5 w-3.5 shrink-0 text-orange-400"
                                                                    aria-hidden="true"
                                                                />
                                                            )}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                    </div>
                                    {selectedOverlayActionValue === 'continue_with_new_goal' && !clarificationOtherChoiceSelected && (
                                        <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 p-3">
                                            <p className="text-xs font-medium text-foreground">
                                                Change goal
                                            </p>
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                Current goal: {selectedBatchCurrentGoalLabel}
                                            </p>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {[
                                                    { value: 'performance', label: 'Performance' },
                                                    { value: 'security', label: 'Security' },
                                                    { value: 'debugging', label: 'Debugging' },
                                                    { value: 'stability', label: 'Stability' }
                                                ].map((goalOption) => {
                                                    const goalValue = goalOption.value as 'performance' | 'security' | 'debugging' | 'stability';
                                                    const isSelectedGoal = selectedGoal === goalValue;
                                                    const isCurrentGoal = goalValue === selectedBatchCurrentGoal;
                                                    const isExhausted = selectedBatchHasGoalContinuationState && selectedBatchExhaustedGoals.has(goalValue);
                                                    const isUsed = selectedBatchHasGoalContinuationState && selectedBatchUsedGoals.has(goalValue);
                                                    const goalDisabled = isCurrentGoal || isExhausted;
                                                    const goalDisabledReason = isCurrentGoal
                                                        ? 'Current goal is already active for this batch.'
                                                        : (isExhausted
                                                            ? 'No fresh issues were found for this goal.'
                                                            : undefined);
                                                    const goalStateClassName = isSelectedGoal
                                                        ? 'border-foreground bg-foreground text-background shadow-sm ring-2 ring-primary/25 hover:bg-foreground/90 dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-zinc-200'
                                                        : isCurrentGoal
                                                            ? 'border-primary/70 bg-primary/10 text-primary shadow-sm ring-1 ring-primary/30 hover:bg-primary/15 disabled:cursor-default disabled:opacity-100'
                                                        : goalDisabled
                                                            ? 'border-border bg-muted text-muted-foreground disabled:opacity-100'
                                                            : 'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground';
                                                    return (
                                                        <Button
                                                            key={goalValue}
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            aria-pressed={isSelectedGoal}
                                                            disabled={isClarificationLoading || goalDisabled}
                                                            title={goalDisabledReason}
                                                            className={cn(
                                                                'min-w-[7.25rem] justify-between gap-2 border font-medium',
                                                                goalStateClassName
                                                            )}
                                                            onClick={() => {
                                                                if (goalDisabled) return;
                                                                onClarificationSelectionChange?.({
                                                                    ...(clarificationSelectionDraft || {}),
                                                                    selectedGoal: goalValue,
                                                                    sourceContextId: currentClarificationOverlayContextId
                                                                });
                                                            }}
                                                        >
                                                            {goalOption.label}
                                                            {isSelectedGoal ? (
                                                                <Check className="h-3.5 w-3.5 shrink-0" style={{ color: 'inherit' }} />
                                                            ) : isCurrentGoal ? (
                                                                <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                                                    Current
                                                                </span>
                                                            ) : isExhausted ? (
                                                                <span className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                                                    No issues
                                                                </span>
                                                            ) : isUsed ? (
                                                                <span className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                                                    Used
                                                                </span>
                                                            ) : null}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                            {normalizedClarificationBatchOptions.length > 0 && (
                                                <div className="mt-3">
                                                    <p className="text-xs font-medium text-foreground">
                                                        Target batch
                                                    </p>
                                                    <p className="mt-1 text-xs text-muted-foreground">
                                                        New plans will stay inside the selected batch.
                                                    </p>
                                                    <div className="mt-2">
                                                        <select
                                                            value={selectedBatchId || ''}
                                                            disabled={isClarificationLoading}
                                                            onChange={(event) => {
                                                                onClarificationSelectionChange?.({
                                                                    ...(clarificationSelectionDraft || {}),
                                                                    selectedBatchId: event.target.value || undefined,
                                                                    sourceContextId: currentClarificationOverlayContextId
                                                                });
                                                            }}
                                                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none"
                                                            aria-label="Select target batch"
                                                        >
                                                            {normalizedClarificationBatchOptions.map((batchOption) => (
                                                                <option key={batchOption.batchId} value={batchOption.batchId}>
                                                                    {batchOption.label}
                                                                    {batchOption.latestGoalLabel ? ` - ${batchOption.latestGoalLabel}` : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {missingRequiredOverlaySelection && !clarificationOtherChoiceSelected && (
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            {requiresGoalSelection
                                                ? selectedGoalMatchesCurrent
                                                    ? 'Choose a different goal and target batch before continuing in the same task.'
                                                    : 'Choose a goal and target batch before continuing in the same task.'
                                                : 'Select the required items in the clarification block above before submitting this action.'}
                                        </p>
                                    )}
                                    {clarificationSelectionResetReason === 'options_updated'
                                        && !selectedClarificationChoice
                                        && clarificationOverlayPhase !== 'strict_verification_recovery' && (
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            Options updated. Choose again before submitting.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
